/**
 * "CRM Partner" on an employee record, classic look: the Employee Details
 * pop-up (HMSAdminDashboard) and the Edit Employee page (AdminEditEmployee).
 * The parent draws the section heading; this is the body. Logic and texts:
 * useEmployeeCrmPartner (shared with the new look).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import HMSButton from '../ui/HMSButton';
import useEmployeeCrmPartner, { approvalLabel, crmProfilePath, summaryRows } from './useEmployeeCrmPartner';
import './hr.css';

export default function EmployeeCrmPartnerBlock({ employeeId, initialProfile = null }) {
  const crm = useEmployeeCrmPartner(employeeId, initialProfile);

  if (crm.loading) {
    return <p className="hr-crm-partner-muted" role="status">Loading CRM partner...</p>;
  }

  if (crm.profile) {
    return (
      <div className="hr-crm-partner">
        <div className="hr-crm-partner-row">
          <span className="hr-crm-partner-label">CRM status:</span>
          <span className="hr-crm-partner-existent">CRM profile existent</span>
        </div>
        <div className="hr-crm-partner-row">
          <span className="hr-crm-partner-label">Partner:</span>
          <span>
            <Link className="hr-crm-partner-link" to={crmProfilePath(crm.profile.id)}>
              {crm.profile.legal_entity_name}
            </Link>
            {approvalLabel(crm.profile) ? (
              <span className="hr-crm-partner-muted"> ({approvalLabel(crm.profile)})</span>
            ) : null}
          </span>
        </div>
      </div>
    );
  }

  if (!crm.summary) {
    return crm.error ? <p className="hr-crm-partner-error" role="alert">{crm.error}</p> : null;
  }

  return (
    <div className="hr-crm-partner">
      <p className="hr-crm-partner-muted">
        No CRM partner yet. Create Partner makes one from these details, marked as Employee. It starts as
        Pending Approval and goes through the normal CRM approval.
      </p>
      <dl className="hr-crm-partner-summary">
        {summaryRows(crm.summary).map((row) => (
          <div className="hr-crm-partner-row" key={row.label}>
            <dt className="hr-crm-partner-label">{row.label}:</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>

      {crm.conflict ? (
        <div className="hr-crm-partner-conflict" role="alert">
          <p>{crm.error}</p>
          <p>
            Existing partner:{' '}
            <Link className="hr-crm-partner-link" to={crmProfilePath(crm.conflict.id)}>
              {crm.conflict.legal_entity_name}
            </Link>
          </p>
          <HMSButton variant="secondary" size="compact" onClick={crm.linkExisting} disabled={crm.busy}>
            {crm.busy ? 'Linking...' : 'Link to this partner'}
          </HMSButton>
        </div>
      ) : (
        <>
          {crm.error ? <p className="hr-crm-partner-error" role="alert">{crm.error}</p> : null}
          <label className="hr-crm-partner-confirm">
            <input
              type="checkbox"
              checked={crm.confirmed}
              onChange={(e) => crm.setConfirmed(e.target.checked)}
            />
            <span>I confirm these details are correct</span>
          </label>
          <HMSButton size="compact" onClick={crm.create} disabled={!crm.confirmed || crm.busy}>
            {crm.busy ? 'Creating...' : 'Create Partner'}
          </HMSButton>
        </>
      )}
    </div>
  );
}
